import { Flag } from '../components/shared/Flag';
import { useFieldErrors } from '../hooks/useFieldErrors';
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import {
  Text, Badge, Button, Spinner, Dropdown, Option, Input, Avatar, Field,
  makeStyles, tokens,
  Dialog, DialogTrigger, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  Checkbox,
} from '@fluentui/react-components';
import { CloudAdd24Regular } from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchUsers, createUser, updateUserRoles, updateUserProfile, updateUserStatus, unlinkUserTelegram, mergeUsers, changeUserPhone, setUserPassword, removeUserPassword, unlockPasswordReset, deleteUser,
  updateUserAllowedLotes, updateUserAllowedRanchos, updateUserDefaultRancho,
} from '../api/settings.api';
import { fetchRanchos } from '../api/rancho.api';
import { fetchTelegramGroups } from '../api/telegram-groups.api';
import { setUserNivelCopia } from '../api/supervision.api';
import { fetchLotes } from '../api/lotes.api';
import { telegramInviteDeepLink } from '../utils/telegram-deeplink';
import { useAuthStore } from '../store/auth.store';
import { RoleSelect, INVITABLE_ROLE_LIST, ASSIGNABLE_ROLE_LIST } from '../components/shared/RoleSelect';
import { PhoneInput } from '../components/shared/PhoneInput';
import { isValidPhoneE164, normalizePhoneInput } from '../utils/validators';
import { useRanchoStore } from '../store/rancho.store';
import { userHasRanchoAccess, userHasAllRanchos } from '../utils/user-rancho-filter';
import { createInvitation, getSelfRegisterCode, generateSelfRegisterCode } from '../api/invitations.api';
import {
  fetchPendingRegistrations, approvePendingRegistration, rejectPendingRegistration,
  type PendingRegistration,
} from '../api/pendingRegistrations';
import { fetchUserActivityReport, type UserActivityRow } from '../api/user-activity.api';
import { DataTable, type DataTableColumn } from '../components/shared/DataTable';
import { useT } from '../i18n';
import { useIntlFormat } from '../hooks/useIntlFormat';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePermission } from '../hooks/usePermission';
import type { Role, UserSetting } from '../types/api.types';
import { PageTitle } from '../components/shared/PageTitle';
import { SubTabs } from '../components/shared/SubTabs';


const ROLE_COLOR: Record<Role, 'brand' | 'success' | 'warning' | 'informative' | 'danger'> = {
  SUPER_ADMIN:       'danger',
  ADMIN:             'warning',
  AGRONOMIST:        'success',
  SUPERVISOR:        'brand',
  WAREHOUSE_MANAGER: 'informative',
  FIELD_WORKER:      'informative',
  AUDITOR:           'informative',
  HR:                'informative',
  GUARD:             'informative',
  INVESTOR:          'informative',
};

const LANG_LABELS: Record<string, string> = {
  es: 'Español',
  en: 'English',
  zh: '中文',
};

const MAX_NAME_LENGTH = 100;
/** Valida el teléfono E.164 que entrega PhoneInput. Retorna mensaje de error o null. */
function validatePhone(phone: string, invalidMsg: string): string | null {
  return isValidPhoneE164(phone) ? null : invalidMsg;
}

function validateName(name: string, t: any): string | null {
  if (!name.trim()) return t.common.validation.nameRequired;
  if (name.trim().length > MAX_NAME_LENGTH) return t.users.nameTooLong.replace('{max}', String(MAX_NAME_LENGTH));
  return null;
}

const useStyles = makeStyles({
  root:  { display: 'flex', flexDirection: 'column', gap: '20px' },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  row: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '10px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ':last-child': { borderBottom: 'none' },
  },
  userInfo: { flex: '0 0 220px', display: 'flex', flexDirection: 'column', gap: '2px' },
  userName: { fontWeight: '600', fontSize: '13px' },
  userEmail: { fontSize: '11px', color: tokens.colorNeutralForeground3 },
  userPhone: { fontSize: '11px', color: tokens.colorNeutralForeground3 },
  telegramStatus: { fontSize: '11px' },
  phone: { flex: '0 0 130px', color: tokens.colorNeutralForeground3, fontSize: '12px' },
  roles: { flex: 1, display: 'flex', gap: '4px', flexWrap: 'wrap' },
  actions: { display: 'flex', gap: '6px', flexShrink: 0 },
  formField: { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' },
  formLabel: { fontSize: '12px', fontWeight: '600', color: tokens.colorNeutralForeground3 },
});

// ─── Invite Link Dialog ───────────────────────────────────────────────────────
/**
 * Link de la empresa (autorregistro): lo mismo que una invitación pero reutilizable y sin precargar
 * nada. Quien entra por aquí queda PENDIENTE hasta que un administrador lo aprueba en Solicitudes.
 */
export function CompanyLinkSection() {
  const t  = useT();
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [error, setError]   = useState('');

  const { data, isLoading } = useQuery({ queryKey: ['self-register-code'], queryFn: getSelfRegisterCode });
  const genMut = useMutation({
    mutationFn: generateSelfRegisterCode,
    onSuccess: () => { setError(''); qc.invalidateQueries({ queryKey: ['self-register-code'] }); },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.users.companyLinkError),
  });
  const link = data?.link ?? '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* sin portapapeles: el admin puede seleccionar el texto */ }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '16px', paddingTop: '12px', borderTop: `1px solid ${tokens.colorNeutralStroke2}` }}>
      <Text weight="semibold">{t.users.companyLinkTitle}</Text>
      <Text style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>{t.users.companyLinkHint}</Text>
      {isLoading ? <Spinner size="tiny" /> : (
        <>
          {link && <Input value={link} readOnly style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px' }} />}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {link && <Button size="small" appearance="outline" onClick={copy}>{copied ? t.users.copied : t.users.copyLink}</Button>}
            <Button size="small" appearance={link ? 'subtle' : 'primary'} disabled={genMut.isPending}
              title={link ? t.users.companyLinkRegenerateHint : undefined} onClick={() => genMut.mutate()}>
              {genMut.isPending ? <Spinner size="tiny" /> : (link ? t.users.companyLinkRegenerate : t.users.companyLinkGenerate)}
            </Button>
          </div>
        </>
      )}
      {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
    </div>
  );
}

function InviteLinkDialog({ onClose, initialRoles }: { onClose: () => void; initialRoles?: Role[] }) {
  const t = useT();
  const isSuperAdmin = useAuthStore(st => !!st.user?.roles?.includes('SUPER_ADMIN'));

  const [roles, setRoles] = useState<Role[]>(initialRoles ?? ['FIELD_WORKER']);
  const [email, setEmail] = useState('');
  const [link,  setLink]  = useState('');
  const [token, setToken] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const inviteMut = useMutation({
    mutationFn: () => createInvitation({ roles, email: email.trim() || undefined }),
    onSuccess: (data) => { setLink(data.link); setToken(data.invitation.token); },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.users.inviteError),
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard no disponible — el usuario puede seleccionar el texto manualmente */ }
  };

  const telegramDeepLink = token ? telegramInviteDeepLink(token) : null;

  const shareMessage = t.users.inviteShareMessage;
  const telegramShareUrl = link
    ? `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(shareMessage)}`
    : '';
  // wa.me sin número de destino abre el selector de contacto/chat del propio
  // WhatsApp del usuario — no requiere conocer el teléfono del invitado de antemano.
  const whatsappShareUrl = link
    ? `https://wa.me/?text=${encodeURIComponent(`${shareMessage} ${link}`)}`
    : '';
  // mailto: no manda nada por sí solo — solo abre el cliente de correo del
  // usuario con el borrador prellenado; el envío real lo hace el admin.
  const mailtoShareUrl = link
    ? `mailto:${email.trim()}?subject=${encodeURIComponent(t.users.inviteEmailSubject)}&body=${encodeURIComponent(`${shareMessage}\n\n${link}`)}`
    : '';

  return (
    <DialogSurface style={{ maxWidth: '440px' }}>
      <DialogBody>
        <DialogTitle>{t.users.inviteTitle}</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%', paddingTop: '8px', gap: '4px' }}>
            {!link ? (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: tokens.colorNeutralForeground3 }}>{t.users.rolesLabel}</label>
                  <RoleSelect roles={roles} onChange={setRoles} options={isSuperAdmin ? ASSIGNABLE_ROLE_LIST : INVITABLE_ROLE_LIST} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: tokens.colorNeutralForeground3 }}>{t.users.emailOptional}</label>
                  <Input
                    placeholder={t.users.emailPlaceholder}
                    value={email}
                    onChange={(_, d) => setEmail(d.value)}
                  />
                </div>
                {error && (
                  <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>
                )}
              </>
            ) : (
              <>
                <Text style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
                  {t.users.inviteShareHint}
                </Text>
                <Input value={link} readOnly style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px' }} />
                {telegramDeepLink && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
                    <Button size="small" appearance="primary" as="a" href={telegramDeepLink}
                      target="_blank" rel="noopener noreferrer">
                      {t.users.inviteTelegramLink}
                    </Button>
                    <Text style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>
                      {t.users.inviteTelegramHint}
                    </Text>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                  <Button size="small" appearance="outline" onClick={handleCopy}>
                    {copied ? t.users.copied : t.users.copyLink}
                  </Button>
                  <Button
                    size="small"
                    appearance="primary"
                    as="a"
                    href={telegramShareUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t.users.shareTelegram}
                  </Button>
                  <Button
                    size="small"
                    appearance="primary"
                    as="a"
                    href={whatsappShareUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t.users.shareWhatsapp}
                  </Button>
                  <Button
                    size="small"
                    appearance="outline"
                    as="a"
                    href={mailtoShareUrl}
                    title={!email.trim() ? t.users.shareEmailHint : undefined}
                  >
                    {t.users.shareEmail}
                  </Button>
                </div>
              </>
            )}
            {/* Con 'Invitar' de una fila (vincular Telegram) no se ofrece: aquí solo para invitar gente nueva. */}
            {!initialRoles && <CompanyLinkSection />}
          </div>
        </DialogContent>
        <DialogActions>
          <Button appearance="subtle" onClick={onClose}>{link ? t.common.cancel : t.common.cancel}</Button>
          {!link && (
            <Button
              appearance="primary"
              onClick={() => inviteMut.mutate()}
              disabled={inviteMut.isPending || !roles.length}
            >
              {inviteMut.isPending ? <Spinner size="tiny" /> : t.users.generateLink}
            </Button>
          )}
        </DialogActions>
      </DialogBody>
    </DialogSurface>
  );
}

// ─── Add User Dialog ──────────────────────────────────────────────────────────
function AddUserDialog({ onClose }: { onClose: () => void }) {
  const s  = useStyles();
  const t  = useT();
  const qc = useQueryClient();

  const [name,     setName]     = useState('');
  const [phone,    setPhone]    = useState('');
  const [roles,    setRoles]    = useState<Role[]>(['FIELD_WORKER']);
  const [language, setLanguage] = useState('es');
  const [error,    setError]    = useState('');
  const fe = useFieldErrors();

  const createMut = useMutation({
    mutationFn: createUser,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.users.createError),
  });

  const handleSubmit = () => {
    setError('');
    const ok = fe.validate({
      name: validateName(name, t),
      phone: !phone.trim() ? t.users.phoneRequired : validatePhone(phone, t.auth.phoneInvalid),
      roles: !roles.length && t.users.rolesRequired,
    });
    if (!ok) return;
    createMut.mutate({ name: name.trim(), phoneNumber: normalizePhoneInput(phone), roles, language });
  };

  return (
    <DialogSurface style={{ maxWidth: '440px' }}>
      <DialogBody>
        <DialogTitle>{t.users.addTitle}</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%', paddingTop: '8px' }}>
            <Field
              className={s.formField}
              label={{ children: `${t.common.name} *`, className: s.formLabel }}
              {...fe.props('name')}
            >
              <Input
                placeholder={t.users.namePlaceholder}
                value={name}
                maxLength={MAX_NAME_LENGTH}
                onChange={(_, d) => { setName(d.value); fe.clear('name'); }}
              />
            </Field>
            <div className={s.formField}>
              <label className={s.formLabel}>{t.users.phoneLabel}</label>
              <PhoneInput
                value={phone}
                error={fe.errors.phone}
                onChange={(v) => { setPhone(v); fe.clear('phone'); }}
                name="agrovid-user-phone"
                ariaLabel={t.users.phoneLabel}
              />
            </div>
            <div className={s.formField}>
              <label className={s.formLabel}>{t.users.rolesLabel}</label>
              <RoleSelect roles={roles} onChange={(r) => { setRoles(r); fe.clear('roles'); }} />
              {fe.errors.roles && <Text size={200} style={{ color: tokens.colorStatusDangerForeground1 }}>{fe.errors.roles}</Text>}
            </div>
            <div className={s.formField}>
              <label className={s.formLabel}>{t.users.languageLabel}</label>
              <Dropdown
                selectedOptions={[language]}
                value={LANG_LABELS[language] ?? language}
                onOptionSelect={(_, d) => setLanguage(d.optionValue ?? 'es')}
                style={{ width: '100%' }}
              >
                <Option value="es" text="Español"><Flag iso="mx" /> Español</Option>
                <Option value="en" text="English"><Flag iso="us" /> English</Option>
                <Option value="zh" text="中文"><Flag iso="cn" /> 中文</Option>
              </Dropdown>
            </div>
            {error && (
              <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button appearance="subtle" onClick={onClose}>{t.common.cancel}</Button>
          <Button
            appearance="primary"
            onClick={handleSubmit}
            disabled={createMut.isPending}
          >
            {createMut.isPending ? <Spinner size="tiny" /> : t.users.addButton}
          </Button>
        </DialogActions>
      </DialogBody>
    </DialogSurface>
  );
}

// ─── Edit User Dialog ─────────────────────────────────────────────────────────
export function EditUserDialog({ user, onClose, isSelf = false }: { user: UserSetting; onClose: () => void; isSelf?: boolean }) {
  const s  = useStyles();
  const t  = useT();
  const qc = useQueryClient();

  const [name,     setName]     = useState(user.name ?? '');
  const [email,    setEmail]    = useState(user.email ?? '');
  const [language, setLanguage] = useState(user.language ?? 'es');
  const [roles,    setRoles]    = useState<Role[]>(user.roles);
  const [error,    setError]    = useState('');
  // Copia de supervisión: nivel 1-3 o sin copia; grupo principal ('' = automático).
  const initialCopy = user.nivelCopia ? String(user.nivelCopia) : 'none';
  const [copyLevel, setCopyLevel] = useState<'none' | '1' | '2' | '3'>(initialCopy as 'none' | '1' | '2' | '3');
  const [mainGroup, setMainGroup] = useState<string>(user.grupoPrincipalId ?? '');
  const { data: telegramGroups = [] } = useQuery({ queryKey: ['telegram-groups'], queryFn: fetchTelegramGroups });
  const supervisionGroups = telegramGroups.filter(g => g.active && g.proposito === 'SUPERVISION');
  const copyChanged = copyLevel !== initialCopy;
  const groupChanged = mainGroup !== (user.grupoPrincipalId ?? '');
  const fe = useFieldErrors();
  const [confirmingSelf, setConfirmingSelf] = useState(false);
  // Quien no tiene teléfono (alta por correo/Google) se identifica por su userId.
  const ref = user.phoneNumber || user.userId || '';
  const emailChanged = email.trim().toLowerCase() !== (user.email ?? '').toLowerCase();

  const saveMut = useMutation({
    mutationFn: async () => {
      await updateUserProfile(ref, {
        name: name.trim(), language,
        // Solo si cambió; vacío quita el correo.
        ...(emailChanged ? { email: email.trim() || null } : {}),
      });
      if (JSON.stringify([...roles].sort()) !== JSON.stringify([...user.roles].sort())) {
        await updateUserRoles(ref, roles);
      }
      if (user.userId && (copyChanged || groupChanged)) {
        await setUserNivelCopia(user.userId, {
          nivelCopia: copyLevel === 'none' ? null : (Number(copyLevel) as 1 | 2 | 3),
          ...(groupChanged ? { grupoPrincipalId: mainGroup || null } : {}),
        });
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.users.saveError),
  });

  const handleSubmit = () => {
    setError('');
    const ok = fe.validate({
      name: validateName(name, t),
      roles: !roles.length && t.users.rolesRequired,
      email: emailChanged && !!email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) && t.auth.emailInvalid,
    });
    if (!ok) return;
    const rolesChanged = JSON.stringify([...roles].sort()) !== JSON.stringify([...user.roles].sort());
    if (isSelf && rolesChanged) {
      const isAdminRole = (r: Role) => r === 'ADMIN' || r === 'SUPER_ADMIN';
      if (user.roles.some(isAdminRole) && !roles.some(isAdminRole)) {
        return setError(t.users.cannotRemoveOwnAdminRole);
      }
      // Cambiar los propios roles revoca la propia sesión: pedir confirmación antes de guardar.
      if (!confirmingSelf) return setConfirmingSelf(true);
    }
    saveMut.mutate();
  };

  return (
    <DialogSurface style={{ maxWidth: '440px' }}>
      <DialogBody>
        <DialogTitle>{t.users.editTitle}</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%', paddingTop: '8px' }}>
            <Field
              className={s.formField}
              label={{ children: `${t.common.name} *`, className: s.formLabel }}
              {...fe.props('name')}
            >
              <Input
                placeholder={t.users.namePlaceholder}
                value={name}
                maxLength={MAX_NAME_LENGTH}
                onChange={(_, d) => { setName(d.value); fe.clear('name'); }}
              />
            </Field>
            <div className={s.formField}>
              <label className={s.formLabel}>{t.users.phoneOnly}</label>
              <Input value={user.phoneNumber || '—'} readOnly disabled />
            </div>
            <Field
              className={s.formField}
              label={{ children: t.users.emailOptional, className: s.formLabel }}
              {...fe.props('email')}
            >
              <Input
                type="email"
                placeholder={t.users.emailPlaceholder}
                value={email}
                maxLength={254}
                onChange={(_, d) => { setEmail(d.value); fe.clear('email'); }}
              />
            </Field>
            <div className={s.formField}>
              <label className={s.formLabel}>{t.users.rolesLabel}</label>
              <RoleSelect roles={roles} onChange={r => { setRoles(r); setConfirmingSelf(false); fe.clear('roles'); }} />
              {fe.errors.roles && <Text size={200} style={{ color: tokens.colorStatusDangerForeground1 }}>{fe.errors.roles}</Text>}
            </div>
            <div className={s.formField}>
              <label className={s.formLabel}>{t.users.languageLabel}</label>
              <Dropdown
                selectedOptions={[language]}
                value={LANG_LABELS[language] ?? language}
                onOptionSelect={(_, d) => setLanguage(d.optionValue ?? 'es')}
                style={{ width: '100%' }}
              >
                <Option value="es" text="Español"><Flag iso="mx" /> Español</Option>
                <Option value="en" text="English"><Flag iso="us" /> English</Option>
                <Option value="zh" text="中文"><Flag iso="cn" /> 中文</Option>
              </Dropdown>
            </div>
            <div className={s.formField}>
              <label className={s.formLabel}>{t.users.copyLabel}</label>
              <Dropdown
                aria-label={t.users.copyLabel}
                disabled={!user.userId}
                value={copyLevel === 'none' ? t.users.copyNone : copyLevel === '1' ? t.users.copyLevel1 : copyLevel === '2' ? t.users.copyLevel2 : t.users.copyLevel3}
                selectedOptions={[copyLevel]}
                onOptionSelect={(_, d) => setCopyLevel((d.optionValue as typeof copyLevel) ?? 'none')}
                style={{ width: '100%' }}
              >
                <Option value="none" text={t.users.copyNone}>{t.users.copyNone}</Option>
                <Option value="1" text={t.users.copyLevel1}>{t.users.copyLevel1}</Option>
                <Option value="2" text={t.users.copyLevel2}>{t.users.copyLevel2}</Option>
                <Option value="3" text={t.users.copyLevel3}>{t.users.copyLevel3}</Option>
              </Dropdown>
              {!user.userId && <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.users.copyNeedsUserId}</Text>}
            </div>
            {copyLevel !== 'none' && (
              <div className={s.formField}>
                <label className={s.formLabel}>{t.users.mainGroupLabel}</label>
                <Dropdown
                  aria-label={t.users.mainGroupLabel}
                  disabled={!user.userId}
                  value={supervisionGroups.find(g => g.chatId === mainGroup)?.label ?? t.users.mainGroupAuto}
                  selectedOptions={[mainGroup]}
                  onOptionSelect={(_, d) => setMainGroup(d.optionValue ?? '')}
                  style={{ width: '100%' }}
                >
                  <Option value="" text={t.users.mainGroupAuto}>{t.users.mainGroupAuto}</Option>
                  {supervisionGroups.map(g => <Option key={g.chatId} value={g.chatId} text={g.label}>{g.label}</Option>)}
                </Dropdown>
              </div>
            )}
            {error && (
              <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>
            )}
            {confirmingSelf && (
              <Text role="alert" style={{ color: tokens.colorStatusWarningForeground1, fontSize: '12px' }}>
                {t.users.selfRolesSessionWarning}
              </Text>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button appearance="subtle" onClick={onClose}>{t.common.cancel}</Button>
          <Button
            appearance="primary"
            onClick={handleSubmit}
            disabled={saveMut.isPending}
          >
            {saveMut.isPending ? <Spinner size="tiny" /> : confirmingSelf ? t.users.confirmAndSave : t.common.save}
          </Button>
        </DialogActions>
      </DialogBody>
    </DialogSurface>
  );
}

// ─── Delete Confirm Dialog ────────────────────────────────────────────────────
// ─── Desbloquear recuperación de contraseña ──────────────────────────────────
/** Botón del administrador: levanta el bloqueo de "pedir código de recuperación" de un usuario. */
function UnlockResetButton({ user }: { user: UserSetting }) {
  const t = useT();
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => unlockPasswordReset(user.phoneNumber),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); },
  });
  // Solo se ofrece si la recuperación está bloqueada o en espera.
  if (!mut.isSuccess && !user.resetStatus) return null;
  if (mut.isSuccess) {
    return <span style={{ color: tokens.colorStatusSuccessForeground1, fontSize: 12 }}>{t.users.unlockResetDone}</span>;
  }
  return (
    <>
      <span data-testid="reset-status" style={{ color: tokens.colorStatusDangerForeground1, fontSize: 12 }}>
        {user.resetStatus === 'locked' ? t.users.resetLockedLabel : t.users.resetWaitingLabel}
      </span>{' '}
      <Button size="small" appearance="transparent" onClick={() => mut.mutate()} disabled={mut.isPending}
              title={mut.isError ? t.users.unlockResetError : undefined}>
        {mut.isPending ? <Spinner size="tiny" /> : mut.isError ? t.users.unlockResetError : t.users.unlockResetButton}
      </Button>
    </>
  );
}

// ─── Desvincular Telegram ────────────────────────────────────────────────────
function UnlinkTelegramDialog({ user, onClose }: { user: UserSetting; onClose: () => void }) {
  const t  = useT();
  const qc = useQueryClient();
  const [error, setError] = useState('');

  const unlinkMut = useMutation({
    mutationFn: () => unlinkUserTelegram(user.phoneNumber),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.users.unlinkTelegramError),
  });

  return (
    <DialogSurface style={{ maxWidth: '400px' }}>
      <DialogBody>
        <DialogTitle>{t.users.unlinkTelegramTitle}</DialogTitle>
        <DialogContent>
          <Text>{t.users.unlinkTelegramBody.replace('{name}', user.name ?? user.phoneNumber)}</Text>
          {error && (
            <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px', display: 'block', marginTop: '8px' }}>
              {error}
            </Text>
          )}
        </DialogContent>
        <DialogActions>
          <Button appearance="subtle" onClick={onClose}>{t.common.cancel}</Button>
          <Button appearance="primary" onClick={() => unlinkMut.mutate()} disabled={unlinkMut.isPending}>
            {unlinkMut.isPending ? <Spinner size="tiny" /> : t.users.unlinkTelegramConfirm}
          </Button>
        </DialogActions>
      </DialogBody>
    </DialogSurface>
  );
}

// ─── Unificar usuarios ───────────────────────────────────────────────────────
// `user` se queda y absorbe a `source`: el servidor rechaza (409) si ambos tienen un
// Telegram, correo o teléfono distinto, y el mensaje se muestra tal cual.
function MergeUsersDialog({ user, candidates, onClose }: { user: UserSetting; candidates: UserSetting[]; onClose: () => void }) {
  const t  = useT();
  const qc = useQueryClient();
  const [sourceId, setSourceId] = useState('');
  const [error, setError] = useState('');
  const label = (u: UserSetting) => [u.name, u.phoneNumber].filter(Boolean).join(' · ') || '—';

  const mergeMut = useMutation({
    mutationFn: () => mergeUsers(user.userId!, sourceId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.users.mergeError),
  });

  return (
    <DialogSurface style={{ maxWidth: '440px' }}>
      <DialogBody>
        <DialogTitle>{t.users.mergeTitle}</DialogTitle>
        <DialogContent>
          <Text>{t.users.mergeBody.replace('{name}', user.name ?? user.phoneNumber)}</Text>
          <Field label={t.users.mergePick} style={{ marginTop: '12px' }}>
            <Dropdown
              placeholder={t.users.mergePickPlaceholder}
              value={candidates.find(c => c.userId === sourceId) ? label(candidates.find(c => c.userId === sourceId)!) : ''}
              selectedOptions={sourceId ? [sourceId] : []}
              onOptionSelect={(_, d) => { setSourceId(d.optionValue ?? ''); setError(''); }}
            >
              {candidates.map(c => (
                <Option key={c.userId} value={c.userId!} text={label(c)}>{label(c)}</Option>
              ))}
            </Dropdown>
          </Field>
          {error && (
            <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px', display: 'block', marginTop: '8px' }}>
              {error}
            </Text>
          )}
        </DialogContent>
        <DialogActions>
          <Button appearance="subtle" onClick={onClose}>{t.common.cancel}</Button>
          <Button appearance="primary" onClick={() => mergeMut.mutate()} disabled={!sourceId || mergeMut.isPending}>
            {mergeMut.isPending ? <Spinner size="tiny" /> : t.users.mergeConfirm}
          </Button>
        </DialogActions>
      </DialogBody>
    </DialogSurface>
  );
}

// ─── Cuenta y acceso (ficha de accesos) ──────────────────────────────────────
// Teléfono, correo, Telegram y contraseña de una persona en un solo lugar. Todo se edita por
// teléfono o, si no tiene, por userId; el servidor valida unicidad y que no quede sin acceso.
type AccessRowProps = {
  label: string;
  value: React.ReactNode;
  children?: React.ReactNode;
};

function AccessRow({ label, value, children }: AccessRowProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '10px 0', borderBottom: `1px solid ${tokens.colorNeutralStroke2}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{label}</Text>
          <Text style={{ overflowWrap: 'anywhere' }}>{value}</Text>
        </div>
      </div>
      {children}
    </div>
  );
}

export function AccountDialog({ user, onClose, onLinkTelegram }: { user: UserSetting; onClose: () => void; onLinkTelegram: () => void }) {
  const t  = useT();
  const qc = useQueryClient();
  const ref = user.phoneNumber || user.userId || '';
  const [phone, setPhone]       = useState(user.phoneNumber ?? '');
  const [email, setEmail]       = useState(user.email ?? '');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');

  const done = () => { setError(''); qc.invalidateQueries({ queryKey: ['users'] }); };
  const fail = (e: any) => setError(e?.response?.data?.error ?? t.users.accessError);

  // Tras cambiar el teléfono el `ref` anterior deja de servir; se cierra para releer la lista.
  const phoneMut    = useMutation({ mutationFn: (v: string | null) => changeUserPhone(ref, v), onSuccess: () => { done(); onClose(); }, onError: fail });
  const emailMut    = useMutation({ mutationFn: (v: string | null) => updateUserProfile(ref, { email: v }), onSuccess: done, onError: fail });
  const pwdSetMut   = useMutation({ mutationFn: () => setUserPassword(ref, password), onSuccess: () => { setPassword(''); done(); }, onError: fail });
  const pwdDelMut   = useMutation({ mutationFn: () => removeUserPassword(ref), onSuccess: done, onError: fail });
  const unlinkMut   = useMutation({ mutationFn: () => unlinkUserTelegram(ref), onSuccess: done, onError: fail });
  const busy = [phoneMut, emailMut, pwdSetMut, pwdDelMut, unlinkMut].some(m => m.isPending);
  const emailOk = !email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  return (
    <DialogSurface style={{ maxWidth: '480px' }}>
      <DialogBody>
        <DialogTitle>{t.users.accountTitle} — {user.name ?? user.phoneNumber}</DialogTitle>
        <DialogContent>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block', marginBottom: '4px' }}>{t.users.accountHint}</Text>

          <AccessRow label={t.users.accessPhone} value={user.phoneNumber || t.users.accessNotSet}>
            <div style={{ display: 'flex', gap: '6px' }}>
              <PhoneInput value={phone} onChange={setPhone} />
              <Button size="small" appearance="primary" disabled={busy || !isValidPhoneE164(normalizePhoneInput(phone)) || normalizePhoneInput(phone) === user.phoneNumber}
                onClick={() => phoneMut.mutate(normalizePhoneInput(phone))}>
                {user.phoneNumber ? t.users.accessChange : t.users.accessAdd}
              </Button>
              {user.phoneNumber && (
                <Button size="small" appearance="subtle" disabled={busy} onClick={() => phoneMut.mutate(null)}>{t.users.accessRemove}</Button>
              )}
            </div>
          </AccessRow>

          <AccessRow label={t.users.accessEmail} value={user.email || t.users.accessNotSet}>
            <div style={{ display: 'flex', gap: '6px' }}>
              <Input type="email" style={{ flex: 1 }} value={email} maxLength={254} placeholder={t.users.emailPlaceholder} onChange={(_, d) => setEmail(d.value)} />
              <Button size="small" appearance="primary" disabled={busy || !email.trim() || !emailOk || email.trim().toLowerCase() === (user.email ?? '').toLowerCase()}
                onClick={() => emailMut.mutate(email.trim())}>
                {user.email ? t.users.accessChange : t.users.accessAdd}
              </Button>
              {user.email && (
                <Button size="small" appearance="subtle" disabled={busy} onClick={() => { setEmail(''); emailMut.mutate(null); }}>{t.users.accessRemove}</Button>
              )}
            </div>
          </AccessRow>

          <AccessRow label={t.users.accessTelegram} value={user.telegramId ? t.users.accessTelegramLinked : t.users.accessNotSet}>
            <div style={{ display: 'flex', gap: '6px' }}>
              {user.telegramId
                ? <Button size="small" appearance="subtle" disabled={busy} onClick={() => unlinkMut.mutate()}>{t.users.unlinkTelegramButton}</Button>
                : <Button size="small" appearance="outline" onClick={onLinkTelegram}>{t.users.accessTelegramLink}</Button>}
            </div>
          </AccessRow>

          <AccessRow label={t.users.accessPassword} value={user.hasPassword ? t.users.accessPasswordSet : t.users.accessPasswordNone}>
            {!user.email && <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.users.accessPasswordNeedsEmail}</Text>}
            <div style={{ display: 'flex', gap: '6px' }}>
              <Input type="password" style={{ flex: 1 }} value={password} autoComplete="new-password" placeholder={t.users.accessPasswordPlaceholder}
                disabled={!user.email} onChange={(_, d) => setPassword(d.value)} />
              <Button size="small" appearance="primary" disabled={busy || !user.email || password.length < 8} onClick={() => pwdSetMut.mutate()}>
                {user.hasPassword ? t.users.accessChange : t.users.accessAdd}
              </Button>
              {user.hasPassword && (
                <Button size="small" appearance="subtle" disabled={busy} onClick={() => pwdDelMut.mutate()}>{t.users.accessRemove}</Button>
              )}
            </div>
          </AccessRow>

          {error && (
            <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px', display: 'block', marginTop: '8px' }}>{error}</Text>
          )}
        </DialogContent>
        <DialogActions>
          <Button appearance="subtle" onClick={onClose}>{t.common.cancel}</Button>
        </DialogActions>
      </DialogBody>
    </DialogSurface>
  );
}

function DeleteConfirmDialog({ user, onClose }: { user: UserSetting; onClose: () => void }) {
  const t  = useT();
  const [warnPre, warnPost = ''] = t.users.deleteActiveWarning.split('{name}');
  const [confirmPre, confirmPost = ''] = t.users.deleteConfirmBody.split('{name}');
  const qc = useQueryClient();

  const isActive = user.status !== 'INACTIVE';

  const [deleteError, setDeleteError] = useState('');
  const deleteMut = useMutation({
    mutationFn: () => deleteUser(user.phoneNumber),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); onClose(); },
    // Sin onError el diálogo se quedaba mudo ante un 400 (p. ej. "No puedes
    // eliminar tu propia cuenta"), igual que las demás mutaciones de la página.
    onError: (e: any) => setDeleteError(e?.response?.data?.error ?? t.users.deleteError),
  });

  return (
    <DialogSurface style={{ maxWidth: '380px' }}>
      <DialogBody>
        <DialogTitle>{t.users.deleteTitle}</DialogTitle>
        <DialogContent>
          {isActive ? (
            <Text>
              {warnPre}<strong>{user.name ?? user.phoneNumber}</strong>{warnPost}
            </Text>
          ) : (
            <Text>
              {confirmPre}<strong>{user.name ?? user.phoneNumber}</strong>{confirmPost}
            </Text>
          )}
          {deleteError && (
            <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px', display: 'block', marginTop: '8px' }}>
              {deleteError}
            </Text>
          )}
        </DialogContent>
        <DialogActions>
          <Button appearance="subtle" onClick={onClose}>{t.common.cancel}</Button>
          <Button
            appearance="primary"
            style={{ backgroundColor: tokens.colorStatusDangerBackground3 }}
            onClick={() => deleteMut.mutate()}
            disabled={isActive || deleteMut.isPending}
          >
            {deleteMut.isPending ? <Spinner size="tiny" /> : t.common.delete}
          </Button>
        </DialogActions>
      </DialogBody>
    </DialogSurface>
  );
}

// ─── Accesos (allowedRanchos / allowedLotes) Dialog ───────────────────────────
export function AccessDialog({ user, onClose }: { user: UserSetting; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();

  const { data: ranchos = [] } = useQuery({ queryKey: ['ranchos'], queryFn: fetchRanchos });
  const lotesQuery = useQuery({ queryKey: ['lotes'], queryFn: () => fetchLotes() });
  const lotes = lotesQuery.data ?? [];

  const [selectedRanchos, setSelectedRanchos] = useState<string[]>(user.allowedRanchos ?? []);
  const [selectedLotes,   setSelectedLotes]   = useState<string[]>(user.allowedLotes ?? []);
  const [ranchosFullAccess, setRanchosFullAccess] = useState(!user.allowedRanchos || user.allowedRanchos.length === 0);
  const [lotesFullAccess,   setLotesFullAccess]   = useState(!user.allowedLotes || user.allowedLotes.length === 0);
  // Rancho activo por defecto — mismo campo que fija /rancho desde el bot.
  // Sin esto, un usuario con 2+ ranchos solo podía elegirlo desde Telegram;
  // un ADMIN ahora puede fijarlo directo desde el dashboard.
  const [defaultRanchoId, setDefaultRanchoId] = useState<string | undefined>(user.defaultRanchoId ?? undefined);

  // El default solo puede ser uno de los ranchos que el usuario efectivamente
  // tiene permitidos — si se restringe a un subconjunto que ya no incluye al
  // default actual, hay que permitir elegir uno nuevo entre los que quedan.
  const defaultRanchoCandidates = ranchosFullAccess ? ranchos : ranchos.filter(r => selectedRanchos.includes(r.ranchoId));

  // Lotes ofrecidos: solo los de los ranchos permitidos (antes se listaban todos
  // los del tenant, sin importar la selección de ranchos). Con acceso a todos los
  // ranchos se rotula cada lote con su rancho: el mismo nombre puede repetirse.
  const lotesInScope = ranchosFullAccess ? lotes : lotes.filter(l => selectedRanchos.includes(l.ranchoId));
  const ranchoNameById = new Map(ranchos.map(r => [r.ranchoId, r.name]));
  const loteLabel = (l: { name: string; ranchoId: string }) =>
    ranchosFullAccess && ranchos.length > 1 ? `${l.name} — ${ranchoNameById.get(l.ranchoId) ?? l.ranchoId}` : l.name;

  // Al restringir ranchos, los lotes ya elegidos que quedan fuera de alcance se quitan.
  // Dos cuidados (mismo patrón que se corrigió en ApprovePendingDialog):
  //  1. Se devuelve el MISMO array si nada cambia: `lotes` vale `[]` (referencia nueva en cada
  //     render) mientras carga, y un array nuevo en cada pasada provocaba un bucle de renders.
  //  2. No se poda hasta que los lotes carguen: con la lista vacía todo "queda fuera de alcance"
  //     y se borraban los lotes ya asignados al usuario (guardar en ese momento los perdía).
  useEffect(() => {
    if (!lotesQuery.isSuccess) return;
    setSelectedLotes(prev => {
      const next = prev.filter(id => lotesInScope.some(l => l.loteId === id));
      return next.length === prev.length ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ranchosFullAccess, selectedRanchos, lotes, lotesQuery.isSuccess]);

  // Si se restringe a un subconjunto que ya no incluye al default actual, se
  // limpia — mostrarlo seleccionado sería engañoso, ya no es válido.
  useEffect(() => {
    if (defaultRanchoId && !defaultRanchoCandidates.some(r => r.ranchoId === defaultRanchoId)) {
      setDefaultRanchoId(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ranchosFullAccess, selectedRanchos]);

  const saveMut = useMutation({
    mutationFn: async () => {
      await updateUserAllowedRanchos(user.phoneNumber, ranchosFullAccess ? null : selectedRanchos);
      await updateUserAllowedLotes(user.phoneNumber, lotesFullAccess ? null : selectedLotes);
      if (defaultRanchoId && defaultRanchoCandidates.some(r => r.ranchoId === defaultRanchoId)) {
        await updateUserDefaultRancho(user.phoneNumber, defaultRanchoId);
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); onClose(); },
  });

  return (
    <DialogSurface style={{ maxWidth: '480px' }}>
      <DialogBody>
        <DialogTitle>Accesos de {user.name ?? user.phoneNumber}</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%', paddingTop: '8px', gap: '16px' }}>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: tokens.colorNeutralForeground3 }}>
                {t.users.pendingRanchosLabel}
              </label>
              <Dropdown
                multiselect
                disabled={ranchosFullAccess}
                selectedOptions={selectedRanchos}
                value={selectedRanchos.map(id => ranchos.find(r => r.ranchoId === id)?.name ?? id).join(', ')}
                onOptionSelect={(_, d) => setSelectedRanchos(d.selectedOptions)}
                placeholder={t.users.ranchosPlaceholder}
                style={{ width: '100%' }}
              >
                {ranchos.map(r => (
                  <Option key={r.ranchoId} value={r.ranchoId}>{r.name}</Option>
                ))}
              </Dropdown>
              <Button
                size="small" appearance="subtle"
                onClick={() => setRanchosFullAccess(v => !v)}
              >
                {ranchosFullAccess ? t.users.accessAllRanchos : t.users.accessRestrictRanchos}
              </Button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: tokens.colorNeutralForeground3 }}>
                {t.users.defaultRanchoLabel}
              </label>
              <Dropdown
                selectedOptions={defaultRanchoId ? [defaultRanchoId] : []}
                value={defaultRanchoId ? (defaultRanchoCandidates.find(r => r.ranchoId === defaultRanchoId)?.name ?? defaultRanchoId) : ''}
                onOptionSelect={(_, d) => setDefaultRanchoId(d.optionValue)}
                placeholder={defaultRanchoCandidates.length === 0 ? t.users.defaultRanchoNone : t.users.defaultRanchoUnset}
                disabled={defaultRanchoCandidates.length === 0}
                style={{ width: '100%' }}
              >
                {defaultRanchoCandidates.map(r => (
                  <Option key={r.ranchoId} value={r.ranchoId}>{r.name}</Option>
                ))}
              </Dropdown>
              <span style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>
                {t.users.defaultRanchoHint}
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: tokens.colorNeutralForeground3 }}>
                {t.users.pendingLotesLabel}
              </label>
              <Dropdown
                multiselect
                disabled={lotesFullAccess}
                selectedOptions={selectedLotes}
                value={selectedLotes.map(id => { const l = lotes.find(x => x.loteId === id); return l ? loteLabel(l) : id; }).join(', ')}
                onOptionSelect={(_, d) => setSelectedLotes(d.selectedOptions)}
                placeholder={t.users.pendingLotesPlaceholder}
                style={{ width: '100%' }}
              >
                {lotesInScope.map(l => (
                  <Option key={l.loteId} value={l.loteId}>{loteLabel(l)}</Option>
                ))}
              </Dropdown>
              <Button
                size="small" appearance="subtle"
                onClick={() => setLotesFullAccess(v => !v)}
              >
                {lotesFullAccess ? t.users.accessAllLotes : t.users.accessRestrictLotes}
              </Button>
            </div>

          </div>
        </DialogContent>
        <DialogActions>
          <Button appearance="subtle" onClick={onClose}>{t.common.cancel}</Button>
          <Button
            appearance="primary"
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending}
          >
            {saveMut.isPending ? <Spinner size="tiny" /> : t.users.saveAccess}
          </Button>
        </DialogActions>
      </DialogBody>
    </DialogSurface>
  );
}

// ─── Approve Pending Registration Dialog ───────────────────────────────────────
/** Un trabajador de campo sin lotes no tiene acceso a ninguno: la solicitud debe traer al menos uno. */
function needsLotes(roles: Role[]): boolean {
  return roles.length === 1 && roles[0] === 'FIELD_WORKER';
}

function ApprovePendingDialog({ contact, onClose }: { contact: PendingRegistration; onClose: () => void }) {
  const t  = useT();
  const qc = useQueryClient();
  const { tenantId } = useParams<{ tenantId: string }>();

  const { data: ranchos = [] } = useQuery({ queryKey: ['ranchos'], queryFn: fetchRanchos });
  const { data: lotes   = [] } = useQuery({ queryKey: ['lotes'],   queryFn: () => fetchLotes() });

  const [roles, setRoles] = useState<Role[]>(['FIELD_WORKER']);
  const [selectedRanchos, setSelectedRanchos] = useState<string[]>([]);
  const [selectedLotes, setSelectedLotes] = useState<string[]>([]);
  const [grantExpensesApprove, setGrantExpensesApprove] = useState(false);
  const [error, setError] = useState('');
  // Solo se puede conceder un permiso que quien aprueba ya tiene (el backend lo vuelve a comprobar).
  const approverCanGrantExpenses = usePermission('expenses:approve');

  // Sin ranchos elegidos se ofrecen los lotes de todos; con ranchos, solo los suyos.
  const lotesInScope = selectedRanchos.length ? lotes.filter(l => selectedRanchos.includes(l.ranchoId)) : lotes;
  const ranchoNameById = new Map(ranchos.map(r => [r.ranchoId, r.name]));
  const loteLabel = (l: { name: string; ranchoId: string }) =>
    ranchos.length > 1 ? `${l.name} — ${ranchoNameById.get(l.ranchoId) ?? l.ranchoId}` : l.name;

  // Al acotar ranchos, los lotes ya elegidos que quedan fuera de alcance se quitan.
  // Se devuelve el MISMO array si nada cambia: `lotes` vale `[]` (referencia nueva en cada
  // render) mientras carga, y un array nuevo en cada pasada provocaba un bucle infinito.
  useEffect(() => {
    setSelectedLotes(prev => {
      const next = prev.filter(id => lotesInScope.some(l => l.loteId === id));
      return next.length === prev.length ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRanchos, lotes]);

  const approveMut = useMutation({
    mutationFn: () => {
      // Con lotes pero sin ranchos, los ranchos salen de los lotes: una lista de ranchos
      // vacía podría leerse como "todos los ranchos".
      const ranchoIds = selectedRanchos.length
        ? selectedRanchos
        : [...new Set(lotes.filter(l => selectedLotes.includes(l.loteId)).map(l => l.ranchoId))];
      return approvePendingRegistration(tenantId!, contact.contactId, {
        roles,
        ...(ranchoIds.length ? { allowedRanchos: ranchoIds } : {}),
        ...(selectedLotes.length ? { allowedLotes: selectedLotes } : {}),
        ...(grantExpensesApprove ? { permissionOverrides: ['expenses:approve'] } : {}),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pendingRegistrations', tenantId] });
      qc.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (e: any) => setError(
      e?.response?.status === 409 ? t.users.pendingAlreadyResolved : (e?.response?.data?.error ?? t.users.pendingApproveError),
    ),
  });

  const loteMissing = needsLotes(roles) && selectedLotes.length === 0;

  return (
    <DialogSurface style={{ maxWidth: '480px' }}>
      <DialogBody>
        <DialogTitle>{t.users.approve}: {[contact.name, contact.lastName].filter(Boolean).join(' ') || contact.contactId}</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%', paddingTop: '8px', gap: '12px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: tokens.colorNeutralForeground3 }}>{t.users.rolesLabel}</label>
              <RoleSelect roles={roles} onChange={setRoles} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: tokens.colorNeutralForeground3 }}>
                {t.users.pendingRanchosLabel}
              </label>
              <Dropdown
                multiselect
                selectedOptions={selectedRanchos}
                value={selectedRanchos.map(id => ranchoNameById.get(id) ?? id).join(', ')}
                onOptionSelect={(_, d) => setSelectedRanchos(d.selectedOptions)}
                placeholder={t.users.pendingRanchosPlaceholder}
                style={{ width: '100%' }}
              >
                {ranchos.map(r => <Option key={r.ranchoId} value={r.ranchoId}>{r.name}</Option>)}
              </Dropdown>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: tokens.colorNeutralForeground3 }}>
                {t.users.pendingLotesLabel}{needsLotes(roles) ? ' *' : ''}
              </label>
              <Dropdown
                multiselect
                selectedOptions={selectedLotes}
                value={selectedLotes.map(id => { const l = lotes.find(x => x.loteId === id); return l ? loteLabel(l) : id; }).join(', ')}
                onOptionSelect={(_, d) => setSelectedLotes(d.selectedOptions)}
                placeholder={t.users.pendingLotesPlaceholder}
                style={{ width: '100%' }}
              >
                {lotesInScope.map(l => <Option key={l.loteId} value={l.loteId}>{loteLabel(l)}</Option>)}
              </Dropdown>
              {loteMissing && (
                <Text style={{ color: tokens.colorStatusWarningForeground1, fontSize: '12px' }}>
                  {t.users.pendingNeedLote}
                </Text>
              )}
            </div>

            {approverCanGrantExpenses && (
              <Checkbox
                checked={grantExpensesApprove}
                onChange={(_, d) => setGrantExpensesApprove(!!d.checked)}
                label={t.users.grantExpensesApprove}
              />
            )}

            {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
          </div>
        </DialogContent>
        <DialogActions>
          <Button appearance="subtle" onClick={onClose}>{t.common.cancel}</Button>
          <Button
            appearance="primary"
            onClick={() => approveMut.mutate()}
            disabled={approveMut.isPending || !roles.length || loteMissing}
          >
            {approveMut.isPending ? <Spinner size="tiny" /> : t.users.confirm}
          </Button>
        </DialogActions>
      </DialogBody>
    </DialogSurface>
  );
}

// ─── Pending Registrations Panel ("Solicitudes" tab) ───────────────────────────
function PendingRegistrationsPanel() {
  const s  = useStyles();
  const t  = useT();
  const { fmtDateOnly } = useIntlFormat();
  const qc = useQueryClient();
  const { tenantId } = useParams<{ tenantId: string }>();

  const { data: pending = [], isLoading } = useQuery({
    queryKey: ['pendingRegistrations', tenantId],
    queryFn: () => fetchPendingRegistrations(tenantId!),
    enabled: !!tenantId,
  });

  const [approveTarget, setApproveTarget] = useState<PendingRegistration | null>(null);
  const [rejectTarget, setRejectTarget] = useState<PendingRegistration | null>(null);
  const [rejectError, setRejectError] = useState('');

  const rejectMut = useMutation({
    mutationFn: (contactId: string) => rejectPendingRegistration(tenantId!, contactId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pendingRegistrations', tenantId] });
      setRejectTarget(null);
    },
    onError: () => setRejectError(t.users.pendingApproveError),
  });

  const displayName = (p: PendingRegistration) => [p.name, p.lastName].filter(Boolean).join(' ') || '—';

  return (
    <div className={s.card}>
      {isLoading ? (
        <Spinner size="small" label={t.users.pendingLoading} />
      ) : pending.length === 0 ? (
        <Text style={{ color: tokens.colorNeutralForeground3 }}>{t.users.pendingEmpty}</Text>
      ) : (
        pending.map((p) => (
          <div key={p.contactId} className={s.row} data-testid="pending-row">
            <Avatar name={displayName(p)} size={36} />
            <div className={s.userInfo}>
              <span className={s.userName}>{displayName(p)}</span>
              {p.phoneNumber && <span className={s.userEmail}>{p.phoneNumber}</span>}
              {p.email && <span className={s.userEmail}>{p.email}</span>}
              <span className={s.userEmail}>
                {p.authMethod === 'google' ? t.users.pendingMethodGoogle : p.authMethod === 'password' ? t.users.pendingMethodPassword : ''}
                {p.createdAt ? ` · ${fmtDateOnly(p.createdAt)}` : ''}
              </span>
            </div>
            <div className={s.actions}>
              <Button size="small" appearance="primary" onClick={() => setApproveTarget(p)}>
                {t.users.approve}
              </Button>
              <Button
                size="small" appearance="subtle"
                style={{ color: tokens.colorStatusDangerForeground1 }}
                onClick={() => { setRejectError(''); setRejectTarget(p); }}
              >
                {t.users.reject}
              </Button>
            </div>
          </div>
        ))
      )}

      <Dialog open={!!approveTarget} onOpenChange={(_, d) => { if (!d.open) setApproveTarget(null); }}>
        <DialogTrigger disableButtonEnhancement><span /></DialogTrigger>
        {approveTarget
          ? <ApprovePendingDialog contact={approveTarget} onClose={() => setApproveTarget(null)} />
          : <></>}
      </Dialog>

      <Dialog open={!!rejectTarget} onOpenChange={(_, d) => { if (!d.open) setRejectTarget(null); }}>
        <DialogSurface style={{ maxWidth: '420px' }}>
          <DialogBody>
            <DialogTitle>{t.users.pendingRejectTitle}</DialogTitle>
            <DialogContent>
              <Text>{t.users.pendingRejectBody.replace('{name}', rejectTarget ? displayName(rejectTarget) : '')}</Text>
              {rejectError && (
                <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px', display: 'block', marginTop: '8px' }}>
                  {rejectError}
                </Text>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="subtle" onClick={() => setRejectTarget(null)}>{t.common.cancel}</Button>
              <Button
                appearance="primary"
                disabled={rejectMut.isPending}
                onClick={() => rejectTarget && rejectMut.mutate(rejectTarget.contactId)}
              >
                {rejectMut.isPending ? <Spinner size="tiny" /> : t.users.reject}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

/** Días transcurridos desde `iso`/epoch ms hasta hoy — null si no hay fecha. */
function daysSince(value: string | number | null): number | null {
  if (!value) return null;
  const then = typeof value === 'number' ? value : new Date(value).getTime();
  return Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24));
}

/**
 * Reporte de actividad de usuarios — combina login del dashboard y actividad
 * de campo por Telegram (`fetchUserActivityReport`) para detectar quién no
 * está trabajando. Ordenado ascendente por `lastSeenAt` (más inactivo primero).
 */
function UserActivityPanel({
  rows,
  isLoading,
  t,
}: {
  rows: UserActivityRow[];
  isLoading: boolean;
  t: ReturnType<typeof useT>;
}) {
  const s = useStyles();

  const columns: DataTableColumn<UserActivityRow>[] = [
    { key: 'name', header: t.users.activityColName, width: 1.4, render: r => r.name ?? '—' },
    { key: 'phoneNumber', header: t.users.activityColPhone, width: 1.2 },
    {
      key: 'telegram', header: t.users.activityColTelegram, align: 'center', width: 0.8,
      render: r => (r.telegramId ? '✅' : '—'),
    },
    {
      key: 'lastSeen', header: t.users.activityColLastSeen, align: 'right', width: 1.2,
      render: r => {
        const days = daysSince(r.lastSeenAt);
        if (days === null) return <span style={{ color: tokens.colorStatusDangerForeground1 }}>{t.users.activityNever}</span>;
        if (days === 0) return t.users.activityToday;
        return (
          <span style={{ color: days > 7 ? tokens.colorStatusDangerForeground1 : undefined }}>
            {t.users.activityDaysAgo.replace('{n}', String(days))}
          </span>
        );
      },
    },
  ];

  return (
    <div className={s.card}>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={r => r.phoneNumber}
        isLoading={isLoading}
        emptyMessage={t.users.activityEmpty}
      />
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export function UsersPage() {
  const myPhone = useAuthStore(st => st.user?.phoneNumber);
  const s  = useStyles();
  const t  = useT();
  usePageTitle(t.pageTitle.users);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const canBulkImport = usePermission('admin:bulk-import');
  const canApprove     = usePermission('users:write');
  const canReadReports = usePermission('reports:read');

  const [activeTab,     setActiveTab]     = useState<'usuarios' | 'solicitudes' | 'actividad'>('usuarios');
  const [showAdd,       setShowAdd]       = useState(false);
  const [showInvite,    setShowInvite]    = useState(false);
  const [deleteTarget,  setDeleteTarget]  = useState<UserSetting | null>(null);
  const [unlinkTarget,  setUnlinkTarget]  = useState<UserSetting | null>(null);
  const [mergeTarget,   setMergeTarget]   = useState<UserSetting | null>(null);
  const [accountTarget, setAccountTarget] = useState<UserSetting | null>(null);
  const [accessTarget,  setAccessTarget]  = useState<UserSetting | null>(null);
  const [editTarget,    setEditTarget]    = useState<UserSetting | null>(null);
  const [inviteTarget,  setInviteTarget]  = useState<UserSetting | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  // Encadenamiento desde el onboarding: MapPage manda aquí tras guardar la
  // geocerca de un lote nuevo con ?openInvite=1 para sugerir invitar a
  // alguien al rancho recién configurado — ver plan
  // docs/superpowers/plans/2026-09-12-onboarding-gaps-bulkimport-invite.md.
  useEffect(() => {
    if (searchParams.get('openInvite') !== '1') return;
    setShowInvite(true);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('openInvite');
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  // Enlace directo desde el aviso de Telegram a una solicitud nueva: /usuarios?tab=solicitudes.
  useEffect(() => {
    if (searchParams.get('tab') !== 'solicitudes' || !canApprove) return;
    setActiveTab('solicitudes');
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('tab');
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams, canApprove]);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn:  fetchUsers,
  });

  // Mismo query key que el panel y el badge del Sidebar: se comparte la caché.
  const { data: pendingList } = useQuery({
    queryKey: ['pendingRegistrations', tenantId],
    queryFn: () => fetchPendingRegistrations(tenantId!),
    enabled: !!tenantId && canApprove,
  });
  const pendingCount = pendingList?.length ?? 0;

  // Selector de rancho de la barra superior: la lista muestra a quien tiene acceso a ese
  // rancho (los restringidos a él + los de acceso total).
  const { activeRanchoId } = useRanchoStore();
  const { data: ranchosForFilter = [] } = useQuery({ queryKey: ['ranchos'], queryFn: fetchRanchos });
  const activeRanchoName = ranchosForFilter.find(r => r.ranchoId === activeRanchoId)?.name;
  const visibleUsers = (users as UserSetting[]).filter(u => userHasRanchoAccess(u, activeRanchoId));

  const { data: activityRows = [], isLoading: activityLoading } = useQuery({
    queryKey: ['user-activity-report'],
    queryFn:  fetchUserActivityReport,
    enabled:  canReadReports && activeTab === 'actividad',
  });

  const statusMut = useMutation({
    mutationFn: ({ phone, status }: { phone: string; status: 'ACTIVE' | 'INACTIVE' }) =>
      updateUserStatus(phone, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  return (
    <div className={s.root}>
      <PageTitle>{t.users.pageTitle}</PageTitle>

      {(canApprove || canReadReports) && (
        <SubTabs
          selected={activeTab}
          onSelect={(v) => setActiveTab(v as 'usuarios' | 'solicitudes' | 'actividad')}
          tabs={[
            { value: 'usuarios', label: t.users.pageTitle },
            ...(canApprove ? [{ value: 'solicitudes', label: `${t.users.pendingRegistrationsTab}${pendingCount > 0 ? ` (${pendingCount})` : ''}` }] : []),
            ...(canReadReports ? [{ value: 'actividad', label: t.users.activityTab }] : []),
          ]}
        />
      )}

      {activeTab === 'solicitudes' && canApprove ? (
        <PendingRegistrationsPanel />
      ) : activeTab === 'actividad' && canReadReports ? (
        <UserActivityPanel rows={activityRows} isLoading={activityLoading} t={t} />
      ) : (
      <div className={s.card}>
        <div className={s.header}>
          <Text weight="semibold" size={400}>{t.users.systemUsers}</Text>
          <div style={{ display: 'flex', gap: '8px' }}>
            {canBulkImport && (
              <Button
                appearance="outline"
                size="small"
                icon={<CloudAdd24Regular />}
                onClick={() => navigate(`/${tenantId}/bulk-import`)}
              >
                {t.nav.bulkImport}
              </Button>
            )}
            <Button appearance="outline" size="small" onClick={() => setShowInvite(true)}>
              {t.users.inviteButton}
            </Button>
            <Button appearance="primary" size="small" onClick={() => setShowAdd(true)}>
              {t.users.addButton}
            </Button>
          </div>
        </div>

        {activeRanchoId && !isLoading && (
          <Text style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, display: 'block', marginBottom: '8px' }}>
            {t.users.ranchoFilterInfo
              .replace('{n}', String(visibleUsers.length))
              .replace('{total}', String((users as UserSetting[]).length))
              .replace('{rancho}', activeRanchoName ?? '')}
          </Text>
        )}

        {isLoading ? (
          <Spinner size="small" label={t.users.loadingUsers} />
        ) : (
          visibleUsers.map((u) => {
            const isInactive = u.status === 'INACTIVE';
            return (
              <div key={u.userId ?? u.phoneNumber} className={s.row} style={{ opacity: isInactive ? 0.55 : 1 }}>

                {/* Avatar + info */}
                <Avatar name={u.name ?? u.phoneNumber} size={36} />

                <div className={s.userInfo}>
                  <span className={s.userName}>{u.name ?? '—'}</span>
                  {u.email && (
                    <span className={s.userEmail}>{u.email}</span>
                  )}
                  <span className={s.userPhone}>{u.phoneNumber}</span>
                  {activeRanchoId && userHasAllRanchos(u) && (
                    <span style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>{t.users.allRanchosBadge}</span>
                  )}
                  <span className={s.telegramStatus}>
                    {u.telegramId
                      ? (
                        <>
                          <span style={{ color: tokens.colorStatusSuccessForeground1 }}>{t.users.telegramLinked}</span>
                          {' '}
                          <Button size="small" appearance="transparent" onClick={() => setUnlinkTarget(u)}>
                            {t.users.unlinkTelegramButton}
                          </Button>
                        </>
                      )
                      : <span style={{ color: tokens.colorNeutralForeground3 }}>✗ {t.topBar.telegramLink.notLinked}</span>}
                    {' '}
                    <UnlockResetButton user={u} />
                  </span>
                </div>

                {/* Roles */}
                <div className={s.roles}>
                  {u.roles.map(r => (
                    <Badge key={r} color={ROLE_COLOR[r]} appearance="tint" size="small">
                      {t.settings.roles[r] ?? r}
                    </Badge>
                  ))}
                </div>

                {/* Acciones */}
                <div className={s.actions}>
                  {!isInactive && (
                    <>
                      <Button size="small" appearance="subtle" onClick={() => setEditTarget(u)}>
                        {t.common.edit}
                      </Button>
                      <Button size="small" appearance="subtle" onClick={() => setAccessTarget(u)}>
                        Accesos
                      </Button>
                      {!u.telegramId && (
                        <Button size="small" appearance="subtle" onClick={() => setInviteTarget(u)}>
                          Invitar
                        </Button>
                      )}
                      <Button size="small" appearance="subtle" onClick={() => setAccountTarget(u)}>
                        {t.users.accountButton}
                      </Button>
                      {u.userId && (users as UserSetting[]).some(o => o.userId && o.userId !== u.userId && o.status !== 'INACTIVE') && (
                        <Button size="small" appearance="subtle" onClick={() => setMergeTarget(u)}>
                          {t.users.mergeButton}
                        </Button>
                      )}
                    </>
                  )}
                  <Button
                    size="small" appearance="subtle"
                    disabled={statusMut.isPending || (!isInactive && u.phoneNumber === myPhone)}
                    title={!isInactive && u.phoneNumber === myPhone ? t.users.cannotDisableSelf : undefined}
                    style={{ color: isInactive ? tokens.colorStatusSuccessForeground1 : tokens.colorStatusWarningForeground1 }}
                    onClick={() => statusMut.mutate({ phone: u.phoneNumber, status: isInactive ? 'ACTIVE' : 'INACTIVE' })}
                  >
                    {isInactive ? t.users.enable : t.users.disable}
                  </Button>
                  {isInactive && (
                    <Button
                      size="small" appearance="subtle"
                      style={{ color: tokens.colorStatusDangerForeground1 }}
                      disabled={u.phoneNumber === myPhone}
                      title={u.phoneNumber === myPhone ? t.users.cannotDeleteSelf : undefined}
                      onClick={() => setDeleteTarget(u)}
                    >
                      {t.common.delete}
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      )}

      {/* Add user dialog */}
      <Dialog open={showAdd} onOpenChange={(_, d) => { if (!d.open) setShowAdd(false); }}>
        <DialogTrigger disableButtonEnhancement><span /></DialogTrigger>
        <AddUserDialog onClose={() => setShowAdd(false)} />
      </Dialog>

      {/* Invite by link dialog */}
      <Dialog open={showInvite} onOpenChange={(_, d) => { if (!d.open) setShowInvite(false); }}>
        <DialogTrigger disableButtonEnhancement><span /></DialogTrigger>
        <InviteLinkDialog onClose={() => setShowInvite(false)} />
      </Dialog>

      {/* Unlink Telegram dialog */}
      <Dialog open={!!unlinkTarget} onOpenChange={(_, d) => { if (!d.open) setUnlinkTarget(null); }}>
        <DialogTrigger disableButtonEnhancement><span /></DialogTrigger>
        {unlinkTarget
          ? <UnlinkTelegramDialog user={unlinkTarget} onClose={() => setUnlinkTarget(null)} />
          : <></>}
      </Dialog>

      {/* Cuenta y acceso (ficha de accesos) */}
      <Dialog open={!!accountTarget} onOpenChange={(_, d) => { if (!d.open) setAccountTarget(null); }}>
        <DialogTrigger disableButtonEnhancement><span /></DialogTrigger>
        {accountTarget
          ? <AccountDialog
              // Los datos se leen de la lista actual para reflejar cada cambio sin cerrar el diálogo.
              user={(users as UserSetting[]).find(o => o.userId && o.userId === accountTarget.userId) ?? accountTarget}
              onClose={() => setAccountTarget(null)}
              onLinkTelegram={() => { setInviteTarget(accountTarget); setAccountTarget(null); }}
            />
          : <></>}
      </Dialog>

      {/* Merge users dialog */}
      <Dialog open={!!mergeTarget} onOpenChange={(_, d) => { if (!d.open) setMergeTarget(null); }}>
        <DialogTrigger disableButtonEnhancement><span /></DialogTrigger>
        {mergeTarget
          ? <MergeUsersDialog
              user={mergeTarget}
              candidates={(users as UserSetting[]).filter(o => o.userId && o.userId !== mergeTarget.userId && o.status !== 'INACTIVE' && (!myPhone || o.phoneNumber !== myPhone))}
              onClose={() => setMergeTarget(null)}
            />
          : <></>}
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(_, d) => { if (!d.open) setDeleteTarget(null); }}>
        <DialogTrigger disableButtonEnhancement><span /></DialogTrigger>
        {deleteTarget
          ? <DeleteConfirmDialog user={deleteTarget} onClose={() => setDeleteTarget(null)} />
          : <></>}
      </Dialog>

      {/* Access (allowedRanchos / allowedLotes) dialog */}
      <Dialog open={!!accessTarget} onOpenChange={(_, d) => { if (!d.open) setAccessTarget(null); }}>
        <DialogTrigger disableButtonEnhancement><span /></DialogTrigger>
        {accessTarget
          ? <AccessDialog user={accessTarget} onClose={() => setAccessTarget(null)} />
          : <></>}
      </Dialog>

      {/* Edit user dialog */}
      <Dialog open={!!editTarget} onOpenChange={(_, d) => { if (!d.open) setEditTarget(null); }}>
        <DialogTrigger disableButtonEnhancement><span /></DialogTrigger>
        {editTarget
          ? <EditUserDialog user={editTarget} isSelf={editTarget.phoneNumber === myPhone} onClose={() => setEditTarget(null)} />
          : <></>}
      </Dialog>

      {/* Invite (per-row, usuario sin Telegram vinculado) dialog */}
      <Dialog open={!!inviteTarget} onOpenChange={(_, d) => { if (!d.open) setInviteTarget(null); }}>
        <DialogTrigger disableButtonEnhancement><span /></DialogTrigger>
        {inviteTarget
          ? <InviteLinkDialog initialRoles={inviteTarget.roles} onClose={() => setInviteTarget(null)} />
          : <></>}
      </Dialog>
    </div>
  );
}
